import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toast } from '@livable/saas-react-components';
import { Button, TextField, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useStyles from './styles';
const initialState = {
  secretCode: '',
  otpAuthUri: '',
  code: '',
  isSubmitting: false,
  associateFailed: false,
  codeRejected: false,
  enableFailed: false
};
export const TotpSetup = _ref => {
  let associate = _ref.associate,
    verify = _ref.verify,
    enable = _ref.enable,
    onEnabled = _ref.onEnabled,
    onCancel = _ref.onCancel;
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState(initialState),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];

  // An unmount while a request is in flight is not a decision by the user (Cancel is disabled for
  // that whole window): a token refresh can navigate staff to /admin, a route can change. Cognito has
  // already consumed the verified code by then, so the enable must still complete or the user is
  // left with a "success" that never happened. Only state updates are skipped after unmount.
  const isMounted = useRef(true);
  useEffect(() => () => {
    isMounted.current = false;
  }, []);
  const startAssociation = () => {
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      associateFailed: false,
      secretCode: '',
      otpAuthUri: ''
    }));
    associate().then(resp => {
      if (!isMounted.current) return;
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) {
        setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
          associateFailed: true
        }));
        return;
      }
      setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        secretCode: resp.response.secretCode,
        otpAuthUri: resp.response.otpAuthUri
      }));
    });
  };

  // Associate on mount, and again only through the explicit retry: every call mints a new secret and
  // would invalidate a QR the user may already have scanned.
  useEffect(() => {
    startAssociation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleCodeChange = event => {
    const code = event.target.value.replace(/\D/g, '').slice(0, 6);
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      code,
      codeRejected: false
    }));
  };

  // Cognito has already accepted the code by this point, so a failed enable is retried on its own:
  // re-verifying would need a fresh code.
  const enableTotp = async () => {
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      isSubmitting: true,
      enableFailed: false
    }));
    try {
      const enabled = await enable({
        method: 'TOTP'
      });
      if (!(enabled !== null && enabled !== void 0 && enabled.isSuccessful)) {
        if (isMounted.current) setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
          enableFailed: true
        }));
        return;
      }
      toast('success', {
        body: [t('mfa.totp_enabled')]
      });
      onEnabled();
    } finally {
      if (isMounted.current) setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        isSubmitting: false
      }));
    }
  };
  const handleConfirm = async () => {
    var _verified;
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      isSubmitting: true,
      codeRejected: false
    }));
    let verified;
    try {
      verified = await verify({
        code: state.code
      });
    } finally {
      if (isMounted.current) setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        isSubmitting: false
      }));
    }
    if (!((_verified = verified) !== null && _verified !== void 0 && _verified.isSuccessful)) {
      if (isMounted.current) setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        codeRejected: true
      }));
      return;
    }
    await enableTotp();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.setupWrapper
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "p",
    marginBottom: "12px"
  }, t('mfa.totp_scan')), state.otpAuthUri && /*#__PURE__*/React.createElement("div", {
    className: classes.qrWrapper
  }, /*#__PURE__*/React.createElement(QRCodeSVG, {
    size: 160,
    value: state.otpAuthUri
  })), state.associateFailed && /*#__PURE__*/React.createElement("div", {
    className: classes.codeField
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "error",
    variant: "body3",
    component: "p",
    marginBottom: "8px"
  }, t('mfa.totp_associate_failed')), /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    onClick: startAssociation,
    variant: "contained",
    'custom-attribute': '307-TotpSetup-retry'
  }, t('mfa.try_again'))), /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "p"
  }, t('mfa.totp_manual_key')), /*#__PURE__*/React.createElement(Typography, {
    className: classes.secret,
    variant: "h6",
    component: "p"
  }, state.secretCode), /*#__PURE__*/React.createElement(TextField, {
    className: classes.codeField,
    disabled: !state.secretCode,
    error: state.codeRejected,
    fullWidth: true,
    helperText: state.codeRejected ? t('mfa.totp_code_rejected') : undefined,
    id: "totpCode",
    inputProps: {
      inputMode: 'numeric'
    },
    label: t('mfa.totp_enter_code'),
    name: "totpCode",
    onChange: handleCodeChange,
    value: state.code
  }), state.enableFailed && /*#__PURE__*/React.createElement("div", {
    className: classes.codeField
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "error",
    variant: "body3",
    component: "p",
    marginBottom: "8px"
  }, t('mfa.totp_enable_failed')), /*#__PURE__*/React.createElement(Button, {
    disabled: state.isSubmitting,
    fullWidth: true,
    onClick: enableTotp,
    variant: "contained",
    'custom-attribute': '308-TotpSetup-retry-enable'
  }, t('mfa.try_again'))), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: state.code.length !== 6 || state.isSubmitting || !state.secretCode || state.enableFailed,
    onClick: handleConfirm,
    variant: "contained",
    'custom-attribute': '303-TotpSetup-confirm'
  }, t('general.confirm')), /*#__PURE__*/React.createElement(Button, {
    disabled: state.isSubmitting,
    onClick: onCancel,
    variant: "text",
    'custom-attribute': '304-TotpSetup-cancel'
  }, t('buttons.cancel'))));
};